import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import { applyFit } from "../lib/mapFit";

/**
 * Issue #212: move the map to the results.
 *
 * `MapContainer`'s `center`/`zoom` are mount-time props — react-leaflet
 * deliberately ignores changes to them — so recentring is necessarily
 * imperative, through `useMap()`. That is the whole reason the search
 * "did nothing": the markers were being filtered correctly underneath a
 * view nobody had ever asked to move.
 *
 * Re-fitting is keyed on `signature` (a value, not object identity) so
 * that a user who has panned somewhere keeps their view through every
 * unrelated re-render — saving a ground, career stats landing — and is
 * only moved when the set of grounds on screen actually changes.
 */
/**
 * #1499 — HOW LONG THE FIRST FIT WAITS FOR THE PERSONAL LISTS. The hold (#1494) lifts when the played and saved
 * lists settle, and an errored list settles. A list that hangs never does, which left the map on the world frame with
 * its pins outside the view for as long as the request hung. Counted from the first rows arriving: when it elapses
 * with the hold still set, the first fit is applied to the rows present. About three seconds is longer than a list
 * normally takes and short enough that a stalled one does not read as an empty map.
 */
export const HOLD_FIRST_ARRIVAL_TIMEOUT_MS = 3000;

export function MapFitter({ fit, signature, reason, refitOnResize = false, reframeOnResize = false, fitFirstArrival = false, holdFirstArrival = false }) {
  const map = useMap();
  const lastSignature = useRef(null);
  /**
   * #643 — WHY THE FIT NEEDS A REASON, and why the row set stopped being one.
   *
   * `signature` alone was enough while the directory was fetched once on
   * mount: the set of grounds on screen changed only when the user searched
   * or filtered, so "the results changed" and "the user asked for something
   * different" were the same event.
   *
   * Viewport fetching breaks that identity, and it breaks it in a loop. The
   * map moves, new rows arrive for the new box, the row set changes, the fit
   * changes, the map moves to it — which loads a different set of rows
   * again. Left alone the map creeps inward until it is framing one ground,
   * and every deliberate zoom-out is yanked straight back in. #212's own
   * words were that a user who has panned somewhere keeps their view; that
   * promise now has to be kept against a row set the panning itself changed.
   *
   * So the fit is applied only when the QUESTION changed — the search term,
   * the chips, near-me — and never merely because the answer arrived. The
   * `signature` guard stays on top of it, so re-asking a question whose
   * answer is in the same place still does not move the map.
   */
  const lastReason = useRef(reason);
  /**
   * #1201 — A MOVE THE USER STARTS CLOSES THE PENDING REASON.
   *
   * A new question with no answer yet stays open (below), so the answer can
   * fit when it lands. That is right only while the map is where the question
   * left it. Tap a count bubble, drag or zoom before the personal lists
   * arrive and the player has already answered with their hands; a fit that
   * then lands over the top of it throws the map back (in the compact band to
   * world view) 100 ms after the tap. So any `movestart` that this component's
   * own `applyFit` did not cause marks the reason consumed.
   *
   * `reasonNow` mirrors the prop for the listener, which outlives renders.
   * `ownMove` is true only for the synchronous span of our own `applyFit`:
   * leaflet raises `movestart` synchronously from `flyTo`/`setView`, and
   * clearing the flag on `moveend` instead would leave it stuck when the fit
   * is a no-op and swallow the player's next move.
   */
  const reasonNow = useRef(reason);
  reasonNow.current = reason;
  const ownMove = useRef(false);
  /**
   * #1477 — THE FIRST ARRIVAL IS NOT A QUESTION. A cold load opens under one reason and the first rows that frame
   * the map land under that same reason (the page's loaded flag flips a render later, with the answer's signature
   * already recorded). The branch below reads that as "not a new question", remembers where the answer is and
   * never applies it, so the map sat on the world default with its pins outside the view. `fitted` is whether
   * this map has ever been framed by the answer, `userMoved` whether the player moved it first; the first answer
   * is applied on arrival unless they did (#1201 still wins over it). Every later answer is as it always was.
   * Opt-in (`fitFirstArrival`, the redrawn band): the shipped page's map is left as it was (D217).
   */
  const fitted = useRef(false);
  const userMoved = useRef(false);
  /**
   * #1499 — the hold's timeout. `latest` is what the timer applies when it fires (the timer outlives renders);
   * `holdTimer` is the pending timeout, set once from the first rows arriving.
   */
  const latest = useRef({ fit, signature, reason });
  latest.current = { fit, signature, reason };
  const holdTimer = useRef(null);
  useEffect(
    () => () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
      // Null, so a StrictMode remount (mount, cleanup, mount) re-arms the guard below.
      holdTimer.current = null;
    },
    [],
  );

  /**
   * #1430 — A CONTAINER THAT CHANGES SIZE IS NOT A MOVE THE USER STARTED, AND IS NOT A NEW QUESTION.
   *
   * Leaflet only follows the window, so a container that changes size on its own (the page widening to the
   * laptop's detail column after the map mounted) leaves it tiling the width it measured. With `refitOnResize`
   * this tells leaflet the container's size changed, and does nothing else: no fit, no reason, no signature,
   * so a view the player moved stays where they put it. `ownMove` is set around it so the #1201 guard above
   * never reads leaflet's size events as the player's move.
   *
   * Only once an arrival fit has been applied (`arrived`). Leaflet's size events are map moves, the page asks
   * about the new frame, and on a map that has not been framed yet that answer is the world's, which is empty
   * (a cold load drawing "No grounds here yet", logged). Cold loads do not need it: the Grounds screen mounts
   * the map after the page has taken its width (`GroundsNearYouScreen`), so it measures the right size.
   */
  const arrived = useRef(false);
  /**
   * #1700 N2 — `reframeOnResize` (the Dashboard preview, which nothing can move) goes one step further than
   * `refitOnResize`: it measures the new size even before the first rows arrive, so the first fit is drawn for
   * the width the box really has, and it frames the same pins again for the new size once they are on the map.
   * The preview mounts before the page has taken its wide layout (768, 1280), so the frame it first drew was for
   * a different width and left the pins off-centre. A view the player moved is kept (`userMoved`): defensive, since the one caller today (the preview) has every way to move the map switched off, so `userMoved` cannot be set there; it is for a future interactive caller.
   */
  useEffect(() => {
    if (!refitOnResize && !reframeOnResize) return undefined;
    const el = map.getContainer();
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      if (!arrived.current && !reframeOnResize) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const size = map.getSize();
      if (Math.round(size.x) === w && Math.round(size.y) === h) return;
      ownMove.current = true;
      try {
        map.invalidateSize();
        const now = latest.current;
        if (reframeOnResize && arrived.current && !userMoved.current && now.fit && now.fit.kind !== "none") {
          applyFit(map, now.fit, { reducedMotion: true });
        }
      } finally {
        ownMove.current = false;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [map, refitOnResize, reframeOnResize]);

  useEffect(() => {
    const onUserMove = () => {
      if (ownMove.current) return;
      userMoved.current = true;
      lastReason.current = reasonNow.current;
    };
    map.on("movestart", onUserMove);
    return () => {
      map.off("movestart", onUserMove);
    };
  }, [map]);

  // Publish the map's REAL view state onto its container. This is what lets
  // the e2e suite assert that searching MOVED THE MAP, rather than that we
  // computed an intention to move it — the distinction this whole issue is
  // about. Written on leaflet's own move/zoom events, so it reports where
  // the map ended up however it got there (fit, pan, scroll-wheel zoom).
  useEffect(() => {
    const publish = () => {
      const el = map.getContainer();
      if (!el) return;
      const c = map.getCenter();
      // Three decimals (~110 m), not four (~11 m). Issue #134's near-me fit
      // centres the map on the player, which writes their position into this
      // attribute — and while nothing in this app exports the DOM, "a child's
      // position, in the DOM, to 11 m" is a sentence worth not being true.
      // Both suites that read it assert to a tolerance far coarser than this
      // (journey 1 needs 0.01°, journey 15 uses toBeCloseTo(…, 0)), so the
      // precision was never load-bearing.
      el.dataset.mapCenter = `${c.lat.toFixed(3)},${c.lng.toFixed(3)}`;
      el.dataset.mapZoom = String(map.getZoom());
    };
    publish();
    map.on("moveend", publish);
    map.on("zoomend", publish);
    return () => {
      map.off("moveend", publish);
      map.off("zoomend", publish);
    };
  }, [map]);

  useEffect(() => {
    /**
     * #1494 — THE FIRST ARRIVAL FIT WAITS FOR THE ANSWER IT IS FRAMING TO BE WHOLE. The directory answer is not the
     * only source of the rows on the map: a ground the player has played or saved reaches it through the personal
     * lists, which land a render or two after the directory answer, and the directory answer itself can be a
     * cached one that predates the newest ground (it is held for 30 s). The first fit used to take whatever the
     * first render held and latch, so a ground that arrived a moment later sat outside the frame for good. While
     * `holdFirstArrival` is set (and the map has never been framed) this effect does nothing at all: nothing is
     * recorded, so the fit lands when the hold lifts exactly as if the rows had arrived then. Once the map has
     * been framed the hold is moot, and a later answer is as it always was.
     */
    if (holdFirstArrival && fitFirstArrival && !fitted.current && !userMoved.current) {
      if (!holdTimer.current && fit && fit.kind !== "none") {
        holdTimer.current = setTimeout(() => {
          holdTimer.current = null;
          if (fitted.current || userMoved.current) return;
          const now = latest.current;
          if (!now.fit || now.fit.kind === "none") return;
          // The same bookkeeping as the first arrival below: a later settle under this reason is recorded, not applied.
          lastReason.current = now.reason;
          lastSignature.current = now.signature;
          ownMove.current = true;
          try {
            applyFit(map, now.fit);
          } finally {
            ownMove.current = false;
          }
          arrived.current = true;
          fitted.current = true;
        }, HOLD_FIRST_ARRIVAL_TIMEOUT_MS);
      }
      return;
    }
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    const firstArrival =
      fitFirstArrival && !fitted.current && !userMoved.current && Boolean(fit) && fit.kind !== "none" && signature !== lastSignature.current;
    if (firstArrival) {
      // Applied on arrival whatever the reason says; the reason and the signature are recorded as if it had been
      // asked, so the flip that follows (same answer) neither fits again nor reads as a new question.
      lastReason.current = reason;
      lastSignature.current = signature;
      ownMove.current = true;
      try {
        applyFit(map, fit);
      } finally {
        ownMove.current = false;
      }
      arrived.current = true;
      fitted.current = true;
      return;
    }
    if (reason === lastReason.current) {
      // Not a new question. Remember where the answer is so that asking the
      // NEXT one does not re-fit to a place the map is already looking at.
      lastSignature.current = signature;
      return;
    }
    // A NEW QUESTION WITH NO ANSWER YET STAYS OPEN. Typing a search changes
    // the question immediately, but the rows that answer it may still be in
    // flight (#643: a search asks about the whole directory, and that is a
    // request). Consuming the reason here would mean the fit never happens —
    // the data lands a moment later against a reason that already looks
    // stale, and the map sits where it was while the card list narrows to
    // one. That is exactly what journeys 1 and 15 caught.
    if (!fit || fit.kind === "none") return;
    lastReason.current = reason;
    if (signature === lastSignature.current) return;
    lastSignature.current = signature;
    ownMove.current = true;
    try {
      applyFit(map, fit);
    } finally {
      ownMove.current = false;
    }
    arrived.current = true;
    fitted.current = true;
  }, [map, fit, signature, reason, fitFirstArrival, holdFirstArrival]);

  return null;
}
